import { useRef, useState } from 'react';
import { GripVertical, Info, Lock, RotateCcw, Smartphone, User as UserIcon } from '../../../icons';
import { Switch } from '../../../components/shared';
import type { MenuAccessState, MenuCatalogItem } from '../../../api/menu-access';
import type { Judgement, Verdict } from '../../../lib/menu-judge';
import { TriState } from './TriState';
import {
  groupBySection, judgeFor, orderedMenus, peopleOf, ruleAt, rulesOf, SECTION_LABEL, sourceText, unitLabel, unitViewer,
  type OrgModel, type Unit,
} from './model';

/**
 * 「부서별 권한」 탭 (확정 시안 v4).
 *
 *   · 회사 전체 — 회사에서 쓰는 메뉴(Switch · 바로 반영) · 규칙이 없는 사람의 결과 · 아래 층 규칙 · **메뉴 차례**(기존 기능 유지)
 *   · 본부 · 팀 — 메뉴마다 상속/허용/차단 · 결과(부서 기준)와 출처 · 그 조직에서 개인 설정이 있는 사람 수
 *   · 팀없는직원 — **잠김**(사용자 결정 Q3 안 B): 회사 설정을 받는다 · 남은 묶음 규칙은 옮기거나 지운다
 *
 * ★ 회사 끔 · 모듈 끔은 최상위다 — 그 메뉴의 「허용」은 잠기고, 이미 걸린 허용은 「적용 안 됨」으로 보인다.
 */
interface Props {
  state: MenuAccessState;
  org: OrgModel | null;
  unit: Unit;
  busy: boolean;
  onCell: (scopeType: 'team' | 'division', scopeId: string, menuKey: string, next: Verdict) => void;
  onClearUnit: () => void;
  onCompanyToggle: (menuKey: string, next: boolean) => void;
  onMove: (menuKey: string, toIndex: number) => void;
  onMigrate: () => void;
  onClearLeftover: () => void;
  onGoPerson: (menuKey: string) => void;
}

type Filter = 'all' | 'ruled' | 'hidden';

const LOCK_REASON: Record<'company' | 'module', string> = {
  company: '회사에서 끈 메뉴는 허용으로 열 수 없습니다',
  module: '계약 모듈이 꺼진 메뉴는 허용으로 열 수 없습니다',
};

export function ResultCell({ org, j }: { org: OrgModel | null; j: Judgement }) {
  return (
    <span className="mpx-res">
      <span className={`mpx-badge ${j.visible ? 'mpx-badge--ok' : 'mpx-badge--bad'}`}>{j.visible ? '보임' : '감춤'}</span>
      <span className="mpx-src">{j.blockedBy === 'module' && <Lock size={11} aria-hidden />} {sourceText(org, j)}</span>
    </span>
  );
}

export function SectionRow({ section, cols }: { section: string; cols: number }) {
  const meta = SECTION_LABEL[section] ?? { title: section };
  return (
    <tr className="mpx-grp">
      <td colSpan={cols}>
        {section === 'mobile' && <Smartphone size={12} aria-hidden />} {meta.title}
        {meta.note && <span className="mpx-dim"> {meta.note}</span>}
      </td>
    </tr>
  );
}

export function Pills({ counts, value, onChange }: { counts: Record<Filter, number>; value: Filter; onChange: (f: Filter) => void }) {
  const items: Array<[Filter, string]> = [['all', '전체'], ['ruled', '규칙 있음'], ['hidden', '감춤']];
  return (
    <div className="mpx-pills" role="group" aria-label="거르기">
      {items.map(([k, t]) => (
        <button key={k} type="button" className="mpx-pill" aria-pressed={value === k} onClick={() => onChange(k)}>
          {t} · {counts[k]}
        </button>
      ))}
    </div>
  );
}

export function DeptTab(props: Props) {
  const { state, org, unit, busy } = props;
  const [filter, setFilter] = useState<Filter>('all');
  const dragKey = useRef<string | null>(null);

  const menus = orderedMenus(state);
  const viewer = unitViewer(org, unit);

  interface Row { m: MenuCatalogItem; j: Judgement; own: Verdict; ruled: boolean }
  const rows: Row[] = menus.map((m) => {
    const j = judgeFor(state, m, viewer);
    let own: Verdict = 'none';
    let ruled = false;
    if (unit.kind === 'team' || unit.kind === 'division') {
      own = ruleAt(state, unit.kind, unit.id, m.menuKey);
      ruled = own !== 'none';
    } else if (unit.kind === 'unassigned') {
      ruled = ruleAt(state, 'unassigned', 'all', m.menuKey) !== 'none';
    } else {
      ruled = state.company.some((c) => c.menuKey === m.menuKey && !c.enabled);
    }
    return { m, j, own, ruled };
  });
  const counts = { all: rows.length, ruled: rows.filter((r) => r.ruled).length, hidden: rows.filter((r) => !r.j.visible).length };
  const shown = rows.filter((r) => (filter === 'ruled' ? r.ruled : filter === 'hidden' ? !r.j.visible : true));
  const shownKeys = new Set(shown.map((r) => r.m.menuKey));
  const groups = groupBySection(menus).map(([s, list]) => [s, list.filter((m) => shownKeys.has(m.menuKey))] as const)
    .filter(([, list]) => list.length > 0);
  const byKey = new Map(rows.map((r) => [r.m.menuKey, r]));

  const unitPeople = org ? peopleOf(org, unit) : [];
  const unitPeopleIds = new Set(unitPeople.map((p) => p.id));
  const overrides = (menuKey: string) =>
    state.rules.filter((r) => r.scopeType === 'user' && r.menuKey === menuKey && unitPeopleIds.has(r.scopeId)).length;

  const empty = <tr><td colSpan={4} className="mpx-dim">이 조건에 맞는 메뉴가 없습니다.</td></tr>;

  /* ── 회사 전체 ── */
  if (unit.kind === 'company') {
    const indexOf = (key: string) => menus.findIndex((m) => m.menuKey === key);
    const sectionOf = (key: string) => menus.find((m) => m.menuKey === key)?.section;
    const moveBy = (key: string, delta: number) => {
      const sec = sectionOf(key);
      const siblings = menus.filter((m) => m.section === sec).map((m) => m.menuKey);
      const i = siblings.indexOf(key);
      const target = siblings[i + delta];
      if (i < 0 || !target) return;
      props.onMove(key, indexOf(target));
    };
    return (
      <>
        <div className="mpx-toolbar"><Pills counts={counts} value={filter} onChange={setFilter} /></div>
        <p className="mpx-note mpx-note--info">
          <Info size={14} aria-hidden />
          <span>회사 층입니다 — 끄면 <b>누구에게도 보이지 않습니다.</b> 본부 · 팀 · 개인의 허용으로도 열리지 않습니다.
            누르면 바로 반영되고 최대 1분 뒤 화면에 닿습니다. 차례는 손잡이를 끌거나 Alt+위/아래로 바꿉니다.</span>
        </p>
        <div className="mpx-scroll">
          <table className="mpx-table">
            <thead>
              <tr><th scope="col">메뉴</th><th scope="col">회사에서 씀</th><th scope="col">규칙이 없는 사람에게</th><th scope="col">아래 층 규칙</th></tr>
            </thead>
            <tbody>
              {groups.length === 0 && empty}
              {groups.map(([section, list]) => [
                <SectionRow key={`s-${section}`} section={section} cols={4} />,
                ...list.map((m) => {
                  const r = byKey.get(m.menuKey)!;
                  const on = !state.company.some((c) => c.menuKey === m.menuKey && !c.enabled);
                  const below = state.rules.filter((x) => x.menuKey === m.menuKey && ['team', 'division', 'user'].includes(x.scopeType));
                  const belowAllow = below.filter((x) => x.allow).length;
                  return (
                    <tr key={m.menuKey} className={r.ruled ? 'is-ruled' : undefined}>
                      <th scope="row">
                        <span className="mpx-mname">
                          <span
                            className="mpx-grip" role="button" tabIndex={0} draggable={!busy}
                            aria-label={`${m.label} 차례 옮기기 — Alt+위/아래`}
                            onDragStart={(e) => { dragKey.current = m.menuKey; e.dataTransfer?.setData('text/plain', m.menuKey); }}
                            onDragEnd={() => { dragKey.current = null; }}
                            onKeyDown={(e) => {
                              if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
                              e.preventDefault();
                              moveBy(m.menuKey, e.key === 'ArrowUp' ? -1 : 1);
                            }}
                          >
                            <GripVertical size={12} aria-hidden />
                          </span>
                          <span
                            className="mpx-mname__t"
                            onDragOver={(e) => { if (dragKey.current && sectionOf(dragKey.current) === m.section) e.preventDefault(); }}
                            onDrop={(e) => {
                              e.preventDefault();
                              const from = dragKey.current;
                              dragKey.current = null;
                              if (from && from !== m.menuKey && sectionOf(from) === m.section) props.onMove(from, indexOf(m.menuKey));
                            }}
                          >{m.label}</span>
                        </span>
                      </th>
                      <td>
                        <Switch checked={on} disabled={busy} aria-label={m.label} onCheckedChange={(next) => props.onCompanyToggle(m.menuKey, next)} />
                      </td>
                      <td><ResultCell org={org} j={r.j} /></td>
                      <td>
                        {!on && belowAllow > 0
                          ? <span className="mpx-tag mpx-tag--warn">허용 {belowAllow}곳 · 적용 안 됨</span>
                          : <span className="mpx-src">{below.length > 0 ? `${below.length}곳에서 따로 정함` : '없음'}</span>}
                      </td>
                    </tr>
                  );
                }),
              ])}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  /* ── 팀없는직원 — 잠김 (Q3 안 B) ── */
  if (unit.kind === 'unassigned') {
    const leftover = rulesOf(state, 'unassigned', 'all');
    return (
      <>
        <div className="mpx-toolbar"><Pills counts={counts} value={filter} onChange={setFilter} /></div>
        <p className="mpx-note mpx-note--warn">
          <Lock size={14} aria-hidden />
          <span><b>팀없는직원에게는 부서 규칙을 걸지 않습니다.</b> 회사 설정을 그대로 받습니다. 한 사람씩 다르게 하려면 「개인별 권한」 탭에서 조정합니다.</span>
        </p>
        {leftover.length > 0 && (
          <div className="mpx-leftover" role="status">
            <span>
              <b>예전 묶음 규칙 {leftover.length}개가 남아 있습니다</b> —{' '}
              {leftover.map((r) => `${menus.find((m) => m.menuKey === r.menuKey)?.label ?? r.menuKey} ${r.allow ? '허용' : '차단'}`).join(' · ')}.
              {' '}이제 판정에 쓰지 않으므로 팀없는직원 {unitPeople.length}명에게는 회사 설정대로 보입니다.
            </span>
            <span className="mpx-leftover__acts">
              <button type="button" className="mpx-btn" disabled={busy} onClick={props.onMigrate}>
                <UserIcon size={13} aria-hidden /> 지금 팀없는직원 {unitPeople.length}명의 개인 설정으로 옮기기
              </button>
              <button type="button" className="mpx-btn mpx-btn--quiet" disabled={busy} onClick={props.onClearLeftover}>
                <RotateCcw size={13} aria-hidden /> 남은 규칙 지우기
              </button>
            </span>
          </div>
        )}
        <div className="mpx-scroll">
          <table className="mpx-table">
            <thead>
              <tr><th scope="col">메뉴</th><th scope="col">부서 설정 (잠김)</th><th scope="col">결과 (회사 설정)</th><th scope="col">개인 설정</th></tr>
            </thead>
            <tbody>
              {groups.length === 0 && empty}
              {groups.map(([section, list]) => [
                <SectionRow key={`s-${section}`} section={section} cols={4} />,
                ...list.map((m) => {
                  const r = byKey.get(m.menuKey)!;
                  const old = ruleAt(state, 'unassigned', 'all', m.menuKey);
                  const ov = overrides(m.menuKey);
                  return (
                    <tr key={m.menuKey}>
                      <th scope="row"><span className="mpx-mname__t">{m.label}</span></th>
                      <td>
                        <span className="mpx-stack">
                          <TriState value="none" onChange={() => {}} label={`${m.label} — 팀없는직원 (잠김)`} disabled />
                          {old !== 'none' && <span className="mpx-src">예전 규칙 {old === 'allow' ? '허용' : '차단'} · 판정에 안 씀</span>}
                        </span>
                      </td>
                      <td><ResultCell org={org} j={r.j} /></td>
                      <td>{ov > 0 ? <button type="button" className="mpx-link" onClick={() => props.onGoPerson(m.menuKey)}>{ov}명 따로</button> : <span className="mpx-src">—</span>}</td>
                    </tr>
                  );
                }),
              ])}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  /* ── 본부 · 팀 ── */
  const scopeType = unit.kind;
  const scopeId = unit.id;
  const mine = rulesOf(state, scopeType, scopeId);
  const name = unitLabel(org, unit);
  const parent = unit.kind === 'team' && viewer.divisionId ? org?.divisions.find((d) => d.id === viewer.divisionId)?.name : null;
  return (
    <>
      <div className="mpx-toolbar">
        <Pills counts={counts} value={filter} onChange={setFilter} />
        <span className="mpx-sp" />
        <button type="button" className="mpx-btn mpx-btn--quiet" disabled={busy || mine.length === 0} onClick={props.onClearUnit}>
          <RotateCcw size={13} aria-hidden /> 이 {unit.kind === 'team' ? '팀' : '본부'}의 규칙 {mine.length}개 지우기
        </button>
      </div>
      <p className="mpx-note mpx-note--info">
        <Info size={14} aria-hidden />
        <span><b>{unit.kind === 'team' ? '이 팀 사람' : '이 본부 모든 팀 사람'}</b>의 기본값입니다. 「상속」은 위 층({parent ? `${parent} › ` : ''}회사)을 그대로 받습니다.
          개인 설정이 있는 사람은 <b>그 메뉴만</b> 개인 설정을 따릅니다.</span>
      </p>
      <div className="mpx-scroll">
        <table className="mpx-table">
          <thead>
            <tr><th scope="col">메뉴</th><th scope="col">{name} 설정</th><th scope="col">결과 (부서 기준)</th><th scope="col">개인 설정</th></tr>
          </thead>
          <tbody>
            {groups.length === 0 && empty}
            {groups.map(([section, list]) => [
              <SectionRow key={`s-${section}`} section={section} cols={4} />,
              ...list.map((m) => {
                const r = byKey.get(m.menuKey)!;
                const ov = overrides(m.menuKey);
                return (
                  <tr key={m.menuKey} className={r.ruled ? 'is-ruled' : undefined}>
                    <th scope="row"><span className="mpx-mname__t">{m.label}</span></th>
                    <td>
                      <span className="mpx-stack">
                        <TriState
                          value={r.own}
                          label={`${m.label} — ${name}`}
                          allowLockedReason={r.j.blockedBy ? LOCK_REASON[r.j.blockedBy] : null}
                          disabled={busy}
                          onChange={(next) => props.onCell(scopeType, scopeId, m.menuKey, next)}
                        />
                        {r.own === 'allow' && r.j.blockedBy && (
                          <span className="mpx-tag mpx-tag--warn">적용 안 됨 · {r.j.blockedBy === 'module' ? '계약 모듈' : '회사에서 끔'}</span>
                        )}
                      </span>
                    </td>
                    <td><ResultCell org={org} j={r.j} /></td>
                    <td>{ov > 0 ? <button type="button" className="mpx-link" onClick={() => props.onGoPerson(m.menuKey)}>{ov}명 따로</button> : <span className="mpx-src">—</span>}</td>
                  </tr>
                );
              }),
            ])}
          </tbody>
        </table>
      </div>
    </>
  );
}
